import React from 'react';
import { Target, Award, Users, TrendingUp, Zap } from 'lucide-react';
import CTA from '../components/CTA';
import { companyInfo } from '../mockData';

const About = () => {
  const values = [
    {
      icon: Target,
      title: 'Our Mission',
      description: 'To empower ecommerce businesses with world-class fulfillment solutions that drive growth and customer satisfaction.'
    },
    {
      icon: Award,
      title: 'Quality First',
      description: 'Every product goes through rigorous quality checks. We stand behind every package we ship with our quality guarantee.'
    },
    {
      icon: Users,
      title: 'Customer Focus',
      description: 'Your success is our success. We provide dedicated support and work as an extension of your team.'
    },
    {
      icon: TrendingUp,
      title: 'Innovation',
      description: 'We continuously invest in technology and processes to provide the most efficient fulfillment solutions.'
    }
  ];

  return (
    <div>
      {/* Modern Hero Section - Orange Theme */}
      <div className="relative bg-gradient-to-br from-gray-900 via-orange-900 to-gray-900 py-40 mt-20 overflow-hidden">
        {/* Grid Background */}
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'linear-gradient(to right, #f97316 1px, transparent 1px), linear-gradient(to bottom, #f97316 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }}></div>
        <div className="absolute top-20 left-10 w-64 h-64 bg-orange-500 rounded-full opacity-20 blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-amber-500 rounded-full opacity-20 blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center space-x-3 bg-orange-500/10 backdrop-blur-sm border border-orange-500/20 rounded-full px-6 py-3 mb-8">
              <Zap size={20} className="text-orange-400" />
              <span className="text-orange-400 font-bold text-sm uppercase tracking-wider">About Our Company</span>
            </div>
            <h1 className="text-6xl md:text-7xl lg:text-8xl font-black text-white mb-6 leading-none">
              <span className="block">Building</span>
              <span className="block bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">The Future of</span>
              <span className="block">Fulfillment</span>
            </h1>
            <p className="text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Your trusted partner in Print on Demand and fulfillment excellence
            </p>
          </div>
        </div>
      </div>

      {/* Story Section - Modern Layout */}
      <section className="py-32 bg-white relative overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Section Header */}
            <div className="mb-12">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-1 h-8 bg-gradient-to-b from-orange-500 to-amber-500"></div>
                <span className="text-orange-600 font-bold text-sm uppercase tracking-widest">Our Story</span>
              </div>
              
              <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-8">
                <span className="block">Delivering</span>
                <span className="block bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">Excellence</span>
                <span className="block">Since Day One</span>
              </h2>
            </div>
            
            <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
              <p className="border-l-4 border-orange-600 pl-6">
                {companyInfo.displayName} was founded with a simple mission: to make world-class 
                fulfillment accessible to businesses of all sizes. We're starting fresh with a commitment 
                to quality, reliability, and exceptional customer service.
              </p>
              <p>
                Our team brings together industry expertise with cutting-edge technology to deliver solutions 
                that truly make a difference. We're building our reputation one successful partnership at a time.
              </p>
              <p className="bg-gradient-to-r from-orange-50 to-amber-50 p-6 rounded-2xl border-l-4 border-orange-600">
                We're more than just a fulfillment company — we're your partners in growth. From 
                product sourcing to global delivery, we handle the complex logistics so you can 
                focus on what matters most: building your brand and delighting your customers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section - Modern Cards */}
      <section className="py-32 bg-gray-50 relative overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-20">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-1 h-8 bg-gradient-to-b from-orange-500 to-amber-500"></div>
              <span className="text-orange-600 font-bold text-sm uppercase tracking-widest">Our Core Values</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-6">
              <span className="block">The Principles</span>
              <span className="block bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">That Guide Us</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl">
              The foundation of everything we do
            </p>
          </div>

          {/* Modern Grid Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              
              return (
                <div
                  key={index}
                  className="group bg-white rounded-2xl p-10 hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-orange-200"
                >
                  {/* Icon */}
                  <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <IconComponent size={28} className="text-white" />
                  </div>

                  <h3 className="text-3xl font-black text-gray-900 mb-4 group-hover:text-orange-600 transition-colors">
                    {value.title}
                  </h3>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <CTA />
    </div>
  );
};

export default About;